import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, FormCheck, Button, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import Slider from 'rc-slider';
import { generateClient } from '@aws-amplify/api';
import 'rc-slider/assets/index.css';
import 'leaflet/dist/leaflet.css';
import useFormFields from '@/hooks/useFormFields';
import { dateField } from '@/commonFields';
import ErrorModal from '@/ErrorModal';
import ProgressHeader from '@/components/ProgressHeader';
import SegmentMap from '@/components/SegmentMap';
import { getAllergies, getConditions, getMedications } from '@/graphql/queries';
import FilterCreator from '@/components/FilterCreator';

const client = generateClient();

const DataSegments = ({ formData: _formData = {}, links = [], setActiveForm, setFormData }) => {
	const navigate = useNavigate();
	const [errorMessage, setErrorMessage] = useState();

	const { formData } = _formData;

	const [segment, setSegment] = useState(formData || {
		ageRange: [null, null],
		ageRangeEnabled: false,
		allergies: [],
		bmi_max: null,
		bmi_min: null,
		bmiEnabled: false,
		conditions: [],
		contactMethod: null,
		contactMethodEnabled: false,
		ethnicity: null,
		ethnicityEnabled: false,
		gender: null,
		genderEnabled: false,
		height_max_in: null,
		height_min_in: null,
		heightEnabled: false,
		interactionFrequency: null,
		interactionFrequencyEnabled: false,
		language: null,
		languageEnabled: false,
		lastInteraction: null,
		lastInteractionEnabled: false,
		locationEnabled: false,
		locationRadius: 47,
		measurementSystem: 'imperial',
		medications: [],
		participationStatus: null,
		participationStatusEnabled: false,
		position: { lat: 40.7, lng: -73.9, label: 'New York' },
		previousTrial: null,
		previousTrialEnabled: false,
		procedures: [],
		race: null,
		raceEnabled: false,
		responseStatus: null,
		responseStatusEnabled: false,
		segmentName: '',
		smoking: null,
		smokingEnabled: false,
		trialPhase: null,
		trialPhaseEnabled: false,
		weight_max_lb: null,
		weight_min_lb: null,
		weightEnabled: false,
		subjectFilterEnabled: false,
		subject_filters_json: null
	});

	const { get_value, set_value, getError } = useFormFields('segment');

	const handleContinue = async () => {

		//navigate('/SegmentSummary', { state: { formData: segment } });
		setFormData({
			oldFormData: _formData.oldFormData,
			formData: segment
		});
		setActiveForm('summary');

	};


	const [allergyOptions, setAllergyOptions] = useState([]);
	const [conditionOptions, setConditionOptions] = useState([]);
	const [medicationOptions, setMedicationOptions] = useState([]);
	const [loading, setLoading] = useState(true);

	const catchError = error => {
		if (error instanceof Error) {
			setErrorMessage(error.toString());
		} else {
			setErrorMessage((error.errors || []).map(e => e.message?.toString()));
		}
	};

	const validateSegmentData = async (segment) => {
		try {
			await segmentSchema.validate(segment, { abortEarly: false });
			return { valid: true, errors: {} };
		} catch (err) {

			const errors = {};
			err.inner.forEach(error => {
				errors[error.path] = error.message;
			});
			return { valid: false, errors };
		}
	};



	const ethnicityOptions = [
		{ label: 'Hispanic or Latino', value: 'Hispanic or Latino' },
		{ label: 'Not Hispanic or Latino', value: 'Not Hispanic or Latino' },
	];



	const raceOptions = [
		{ label: 'African American', value: 'African American' },
		{ label: 'African-American', value: 'African-American' },
		{ label: 'American-Indian', value: 'American-Indian' },
		{ label: 'Asian', value: 'Asian' },
		{ label: 'Asian or Pacific Islander', value: 'Asian or Pacific Islander' },
		{ label: 'Black, not of Hispanic Or', value: 'Black, not of Hispanic Or' },
		{ label: 'Caucasian', value: 'Caucasian' },
		{ label: 'Chinese', value: 'Chinese' },
		{ label: 'East Indian', value: 'East Indian' },
		{ label: 'Filipino', value: 'Filipino' },
		{ label: 'Hispanic', value: 'Hispanic' },
		{ label: 'Japanese', value: 'Japanese' },
		{ label: 'Multiracial', value: 'Multiracial' },
		{ label: 'Native Alaskan', value: 'Native Alaskan' },
		{ label: 'Native American', value: 'Native American' },
		{ label: 'Native Hawaiian', value: 'Native Hawaiian' },
		{ label: 'Other', value: 'Other' },
		{ label: 'Unclassified', value: 'Unclassified' },
		{ label: 'Unknown', value: 'Unknown' },
		{ label: 'White', value: 'White' },
		{ label: 'White, not of Hispanic Or', value: 'White, not of Hispanic Or' },
	];


	const languageOptions = [
		{ label: 'Prefer not to say', value: null },
		{ label: 'English', value: 'English' },
		{ label: 'Spanish', value: 'Spanish' },
		{ label: 'Other', value: 'Other' }
	];


	const participationStatusOptions = [
		{ label: 'Active', value: 'Active' },
		{ label: 'Withdrawn', value: 'Withdrawn' }
	];


	useEffect(() => {
		async function fetchData() {
			try {
				const allergyRes = await client.graphql({ query: getAllergies, variables: { sorting: [{ field: 'name' }] } });
				const conditionRes = await client.graphql({ query: getConditions, variables: { sorting: [{ field: 'name' }] } });
				const medicationRes = await client.graphql({ query: getMedications, variables: { sorting: [{ field: 'name' }] } });

				setAllergyOptions(
					allergyRes.data.getAllergies.allergies);
				setConditionOptions(conditionRes.data.getConditions.conditions);
				setMedicationOptions(medicationRes.data.getMedications.medications);
				setLoading(false);
			} catch (error) {
				console.error('Error loading data:', error);
				setLoading(false);
			}
		}
		fetchData();
	}, []);





	const handleChange = (field, value) => {
		setSegment(prev => ({
			...prev,
			[field]: value,
		}));
	};


	return (
		<>
			{errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage()} title="Error" />}
			<ProgressHeader step={1} />
			<div className="container mt-3">
				<h4>General Information</h4>
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Segment Name
					</Form.Label>

					<Col md={{ span: 8, offset: 4 }}>
						<Form.Control
							autoFocus={!segment.id}
							type="text"
							value={segment.segmentName}
							onChange={e => handleChange('segmentName', e.target.value)}
							placeholder="Start typing…"
							style={{
								width: '70%',
								marginLeft: 'auto',
								backgroundColor: '#f5f5f5',
								border: 'none',
								borderRadius: '8px',
								padding: '10px 15px',
								color: '#333',
							}}
						/>
					</Col>
				</Row>


				<h4 style={{ marginTop: '20px' }}>Demographic & Identity Criteria</h4>


				{/* Age Range with enable/disable checkbox */}
				<Row className="mt-3 mb-5" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Age Range
					</Form.Label>

					<Col
						md={{ span: 8, offset: 4 }}
						style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
					>
						<FormCheck
							id="ageRangeEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.ageRangeEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								handleChange('ageRangeEnabled', checked);
								if (!checked) {
									handleChange('ageRange', [null, null]);
								} else if (!segment.ageRange || segment.ageRange.includes(null)) {

									handleChange('ageRange', [18, 65]);
								}
							}}
							style={{ marginLeft: '100px' }}
						/>

						<Slider
							range
							min={18}
							max={65}
							value={segment.ageRangeEnabled ? segment.ageRange : [null, null]}
							marks={{
								18: '18',
								25: '25',
								35: '35',
								45: '45',
								55: '55',
								60: '60',
								65: '65+',
							}}
							onChange={(val) => handleChange('ageRange', val)}
							style={{ width: '70%', marginLeft: 'auto' }}
							step={null}
							disabled={!segment.ageRangeEnabled}
						/>
					</Col>
				</Row>





				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Gender
					</Form.Label>

					<Col
						md={{ span: 8, offset: 4 }}
						style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
					>
						<FormCheck
							id="genderEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.genderEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								handleChange('genderEnabled', checked);
								if (!checked) {
									handleChange('gender', null);
								} else if (!segment.gender) {

									handleChange('gender', 'Other');
								}
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div
							style={{
								width: '70%',
								marginLeft: 'auto',
								backgroundColor: '#f5f5f5',
								borderRadius: '8px',
								padding: '4px',
								display: 'flex',
								justifyContent: 'space-between',
							}}
						>
							{['Male', 'Female', 'Non-binary', 'Other'].map((val) => (
								<Button
									key={val}
									variant="light"
									onClick={() => handleChange('gender', val)}
									disabled={!segment.genderEnabled}
									style={{
										flex: 1,
										backgroundColor: segment.gender === val ? '#fff' : '#f5f5f5',
										border: 'none',
										color: segment.gender === val ? 'black' : '#666',
										fontWeight: segment.gender === val ? '600' : 'normal',
										boxShadow: segment.gender === val ? '0 0 0 2px rgba(0, 0, 0, 0.1)' : 'none',
										borderRadius: '6px',
										margin: '0 4px',
										padding: '6px 12px',
									}}
								>
									{val}
								</Button>
							))}
						</div>
					</Col>
				</Row>



				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Race
					</Form.Label>

					<Col
						md={{ span: 8, offset: 4 }}
						style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
					>
						<FormCheck
							id="raceEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.raceEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								handleChange('raceEnabled', checked);
								if (!checked) {
									handleChange('race', null);
								} else if (!segment.race) {

									handleChange('race', null);
								}
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div style={{ width: '70%', marginLeft: 'auto' }}>
							<Select
								options={raceOptions}
								value={raceOptions.find(f => f.value === segment.race)}
								onChange={val => handleChange('race', val)}
								placeholder="Select race..."
								isDisabled={!segment.raceEnabled}
								styles={{
									control: (provided) => ({
										...provided,
										backgroundColor: '#f5f5f5',
										borderColor: '#ccc',
										minHeight: '38px',
										boxShadow: 'none',
										'&:hover': { borderColor: '#bbb' },
									}),
									placeholder: (provided) => ({
										...provided,
										color: '#888',
									}),
									menu: (provided) => ({
										...provided,
										zIndex: 9999,
										position: 'absolute',
									}),
								}}
							/>
						</div>
					</Col>
				</Row>

				{/* Ethnicity/Race */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Ethnicity
					</Form.Label>

					<Col
						md={{ span: 8, offset: 4 }}
						style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
					>
						<FormCheck
							id="ethnicityEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.ethnicityEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								handleChange('ethnicityEnabled', checked);
								if (!checked) {
									handleChange('ethnicity', null);
								} else if (!segment.ethnicity) {
									handleChange('ethnicity', null);
								}
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div style={{ width: '70%', marginLeft: 'auto' }}>
							<Select
								options={ethnicityOptions}
								value={ethnicityOptions.find(f => f.value === segment.ethnicity)}
								onChange={val => handleChange('ethnicity', val)}
								placeholder="Select ethnicity..."
								isDisabled={!segment.ethnicityEnabled}
								styles={{
									control: (provided) => ({
										...provided,
										backgroundColor: '#f5f5f5',
										borderColor: '#ccc',
										minHeight: '38px',
										boxShadow: 'none',
										'&:hover': { borderColor: '#bbb' },
									}),
									placeholder: (provided) => ({
										...provided,
										color: '#888',
									}),
									menu: (provided) => ({
										...provided,
										zIndex: 9999,
										position: 'absolute',
									}),
								}}
							/>
						</div>
					</Col>
				</Row>


				{/* Language Preferences */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
							whiteSpace: 'nowrap',
						}}
					>
						Language Preferences
					</Form.Label>

					<Col
						md={{ span: 8, offset: 4 }}
						style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
					>
						<FormCheck
							id="languageEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.languageEnabled ?? true}
							onChange={e => {
								const checked = e.target.checked;
								handleChange('languageEnabled', checked);
								if (!checked) {
									handleChange('language', null);
								} else if (!segment.language) {
									handleChange('language', null);
								}
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div style={{ width: '70%', marginLeft: 'auto' }}>
							<Select
								options={languageOptions}
								value={languageOptions.find(f => f.value === segment.language)}
								onChange={val => handleChange('language', val)}
								isDisabled={!segment.languageEnabled}
								styles={{
									control: (provided) => ({
										...provided,
										backgroundColor: '#f5f5f5',
										borderColor: '#ccc',
										minHeight: '38px',
										boxShadow: 'none',
										'&:hover': { borderColor: '#bbb' },
									}),
									placeholder: (provided) => ({
										...provided,
										color: '#888',
									}),
									menu: (provided) => ({
										...provided,
										zIndex: 9999,
										position: 'absolute',
									}),
								}}
							/>
						</div>
					</Col>
				</Row>



				<SegmentMap
					enabled={segment.locationEnabled ?? true}
					locationRadius={segment.locationRadius}
					measurementSystem={segment.measurementSystem}
					onSegmentChange={handleChange}
					position={segment.position}
					setEnabled={enabled => setSegment(prev => ({ ...prev, locationEnabled: enabled }))}
				/>


				<h4 className="mt-4">Health & Medical Profile</h4>


				{/* BMI */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						BMI
					</Form.Label>

					<Col md={{ span: 8, offset: 4 }} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<FormCheck
							id="bmiEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.bmiEnabled ?? true}
							onChange={e => {
								const checked = e.target.checked;
								setSegment(prev => ({
									...prev,
									bmiEnabled: checked,
									bmi_min: checked ? prev.bmi_min : null,
									bmi_max: checked ? prev.bmi_max : null,
								}));
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div style={{ width: '70%', marginLeft: 'auto' }}>
							<Row>
								{['min', 'max'].map((key) => (
									<Col key={key}>
										<div
											style={{
												backgroundColor: '#f5f5f5',
												borderRadius: '8px',
												display: 'flex',
												alignItems: 'center',
												padding: '4px 8px',
											}}
										>
											<span style={{ marginRight: '8px', fontSize: '0.9em', color: '#555' }}>
												{key === 'min' ? 'Min' : 'Max'}
											</span>
											<Form.Control
												type="number"
												step="0.1"
												placeholder="0.0"
												value={segment[`bmi_${key}`] ?? ''}
												onChange={e =>
													setSegment(prev => ({
														...prev,
														[`bmi_${key}`]: e.target.value,
													}))
												}
												style={{
													backgroundColor: '#f5f5f5',
													border: 'none',
													boxShadow: 'none',
													flex: 1,
												}}
												disabled={!segment.bmiEnabled}
											/>
										</div>
									</Col>
								))}
							</Row>
						</div>
					</Col>
				</Row>


				{/* Weight (lbs) */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Weight
					</Form.Label>

					<Col md={{ span: 8, offset: 4 }} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<FormCheck
							id="weightEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.weightEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								setSegment(prev => ({
									...prev,
									weightEnabled: checked,
									weight_min_lb: checked ? (prev.weight_min_lb ?? '') : null,
									weight_max_lb: checked ? (prev.weight_max_lb ?? '') : null,
								}));
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div style={{ width: '70%', marginLeft: 'auto' }}>
							<Row>
								{['min', 'max'].map((key) => (
									<Col key={key}>
										<div
											style={{
												backgroundColor: '#f5f5f5',
												borderRadius: '8px',
												display: 'flex',
												alignItems: 'center',
												padding: '4px 8px',
											}}
										>
											<span style={{ marginRight: '8px', fontSize: '0.9em', color: '#555' }}>
												{key === 'min' ? 'Min' : 'Max'}
											</span>
											<Form.Control
												type="number"
												placeholder="0.00"
												value={segment[`weight_${key}_lb`] ?? ''}
												onChange={e =>
													setSegment(prev => ({
														...prev,
														[`weight_${key}_lb`]: e.target.value,
													}))
												}
												style={{
													backgroundColor: '#f5f5f5',
													border: 'none',
													boxShadow: 'none',
													flex: 1,
												}}
												disabled={!segment.weightEnabled}
											/>
											<span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#555' }}>
												Pound
											</span>
										</div>
									</Col>
								))}
							</Row>
						</div>
					</Col>
				</Row>


				{/* Height (inches) */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Height
					</Form.Label>

					<Col md={{ span: 8, offset: 4 }} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<FormCheck
							id="heightEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.heightEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								setSegment(prev => ({
									...prev,
									heightEnabled: checked,
									height_min_in: checked ? (prev.height_min_in ?? '') : null,
									height_max_in: checked ? (prev.height_max_in ?? '') : null,
								}));
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div style={{ width: '70%', marginLeft: 'auto' }}>
							<Row>
								{['min', 'max'].map((key) => (
									<Col key={key}>
										<div
											style={{
												backgroundColor: '#f5f5f5',
												borderRadius: '8px',
												display: 'flex',
												alignItems: 'center',
												padding: '4px 8px',
											}}
										>
											<span style={{ marginRight: '8px', fontSize: '0.9em', color: '#555' }}>
												{key === 'min' ? 'Min' : 'Max'}
											</span>
											<Form.Control
												type="number"
												placeholder="0"
												value={segment[`height_${key}_in`] ?? ''}
												onChange={e =>
													setSegment(prev => ({
														...prev,
														[`height_${key}_in`]: e.target.value,
													}))
												}
												style={{
													backgroundColor: '#f5f5f5',
													border: 'none',
													boxShadow: 'none',
													flex: 1,
												}}
												disabled={!segment.heightEnabled}
											/>
											<span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#555' }}>
												Inch
											</span>
										</div>
									</Col>
								))}
							</Row>
						</div>
					</Col>
				</Row>


				{/* Conditions Diagnosed */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
							whiteSpace: 'nowrap',
						}}
					>
						Conditions Diagnosed
					</Form.Label>
					<Col md={{ span: 8, offset: 4 }}>
						<div
							style={{
								width: '70%',
								marginLeft: 'auto',
								backgroundColor: '#f5f5f5',
								borderRadius: '8px',
								padding: '4px 8px',
							}}
						>
							<Select
								getOptionLabel={option => option.name}
								getOptionValue={option => option.id}
								isMulti
								options={conditionOptions}
								value={segment.conditions}
								onChange={val => handleChange('conditions', val)}
								placeholder="Select conditions..."
								styles={{
									control: (provided) => ({
										...provided,
										backgroundColor: '#f5f5f5',
										border: 'none',
										boxShadow: 'none',
										minHeight: '38px',
									}),
									placeholder: (provided) => ({
										...provided,
										color: '#888',
									}),
									menu: (provided) => ({
										...provided,
										zIndex: 9999,
									}),
								}}
							/>
						</div>
					</Col>
				</Row>


				{/* Previous Treatment or Procedures */}
				{/*		 
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
					style={{
						position: 'absolute',
						left: 0,
						top: '50%',
						transform: 'translateY(-50%)',
						width: '200px',
						textAlign: 'left',
						fontWeight: '500',
						whiteSpace: 'nowrap',
					}}
					>
					Previous Treatment or Procedures
					</Form.Label>
					<Col md={{ span: 8, offset: 4 }}>
					<div
						style={{
						width: '70%',
						marginLeft: 'auto',
						backgroundColor: '#f5f5f5',
						borderRadius: '8px',
						padding: '4px 8px',
						}}
					>
						<Select
						isMulti
						options={procedureOptions}
						value={segment.procedures}
						onChange={val => handleChange('procedures', val)}
						placeholder="Select previous treatment..."
						styles={{
							control: (provided) => ({
							...provided,
							backgroundColor: '#f5f5f5',
							border: 'none',
							boxShadow: 'none',
							minHeight: '38px',
							}),
							placeholder: (provided) => ({
							...provided,
							color: '#888',
							}),
							menu: (provided) => ({
							...provided,
							zIndex: 9999,
							}),
						}}
						/>
					</div>
					</Col>
				</Row> */}

				{/* Current Medications */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '200px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Current Medications
					</Form.Label>
					<Col md={{ span: 8, offset: 4 }}>
						<div
							style={{
								width: '70%',
								marginLeft: 'auto',
								backgroundColor: '#f5f5f5',
								borderRadius: '8px',
								padding: '4px 8px',
							}}
						>
							<Select
								getOptionLabel={option => option.name}
								getOptionValue={option => option.id}
								isMulti
								options={medicationOptions}
								value={segment.medications}
								onChange={val => handleChange('medications', val)}
								placeholder="Select medications..."
								styles={{
									control: (provided) => ({
										...provided,
										backgroundColor: '#f5f5f5',
										border: 'none',
										boxShadow: 'none',
										minHeight: '38px',
									}),
									placeholder: (provided) => ({
										...provided,
										color: '#888',
									}),
									menu: (provided) => ({
										...provided,
										zIndex: 9999,
									}),
								}}
							/>
						</div>
					</Col>
				</Row>

				{/* Allergies */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '200px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Allergies
					</Form.Label>
					<Col md={{ span: 8, offset: 4 }}>
						<div
							style={{
								width: '70%',
								marginLeft: 'auto',
								backgroundColor: '#f5f5f5',
								borderRadius: '8px',
								padding: '4px 8px',
							}}
						>
							<Select
								getOptionLabel={option => option.name}
								getOptionValue={option => option.id}
								isMulti
								options={allergyOptions}
								value={segment.allergies}
								onChange={(val) => handleChange('allergies', val)}
								placeholder="Select allergies..."
								styles={{
									control: (provided) => ({
										...provided,
										backgroundColor: '#f5f5f5',
										border: 'none',
										boxShadow: 'none',
										minHeight: '38px',
									}),
									placeholder: (provided) => ({
										...provided,
										color: '#888',
									}),
									menu: (provided) => ({
										...provided,
										zIndex: 9999,
									}),
								}}
							/>
						</div>
					</Col>
				</Row>



				{/* Smoking Status */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Smoking Status
					</Form.Label>

					<Col md={{ span: 8, offset: 4 }} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<FormCheck
							id="smokingEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.smokingEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								handleChange('smokingEnabled', checked);
								if (!checked) {
									handleChange('smoking', null);
								} else if (!segment.smoking) {
									handleChange('smoking', 'Non-Smoker');
								}
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div
							style={{
								width: '70%',
								marginLeft: 'auto',
								backgroundColor: '#f5f5f5',
								borderRadius: '8px',
								padding: '4px',
								display: 'flex',
								justifyContent: 'space-between',
							}}
						>
							{['Smoker', 'Non-Smoker', 'Former Smoker'].map((val) => (
								<Button
									key={val}
									variant="light"
									onClick={() => handleChange('smoking', val)}
									disabled={!segment.smokingEnabled}
									style={{
										flex: 1,
										backgroundColor: segment.smoking === val ? '#fff' : '#f5f5f5',
										border: 'none',
										color: segment.smoking === val ? 'black' : '#666',
										fontWeight: segment.smoking === val ? '600' : 'normal',
										boxShadow: segment.smoking === val ? '0 0 0 2px rgba(0, 0, 0, 0.1)' : 'none',
										borderRadius: '6px',
										margin: '0 4px',
										padding: '6px 12px',
									}}
								>
									{val}
								</Button>
							))}
						</div>
					</Col>
				</Row>


				<h4 className="mt-4">Trial Participation & History</h4>

				{/* Previous Trial Participation */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
							whiteSpace: 'nowrap',
						}}
					>
						Previous Trial Participation
					</Form.Label>

					<Col
						md={{ span: 8, offset: 4 }}
						style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
					>
						<FormCheck
							id="previousTrialEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.previousTrialEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								handleChange('previousTrialEnabled', checked);
								if (!checked) {
									handleChange('previousTrial', null);
								} else if (!segment.previousTrial) {
									handleChange('previousTrial', 'None');
								}
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div
							style={{
								width: '70%',
								marginLeft: 'auto',
								backgroundColor: '#f5f5f5',
								borderRadius: '8px',
								padding: '4px',
								display: 'flex',
								justifyContent: 'space-between',
							}}
						>
							{['None', 'Ongoing', 'Completed'].map((val) => (
								<Button
									key={val}
									variant="light"
									onClick={() => handleChange('previousTrial', val)}
									disabled={!segment.previousTrialEnabled}
									style={{
										flex: 1,
										backgroundColor: segment.previousTrial === val ? '#fff' : '#f5f5f5',
										border: 'none',
										color: segment.previousTrial === val ? 'black' : '#666',
										fontWeight: segment.previousTrial === val ? '600' : 'normal',
										boxShadow: segment.previousTrial === val ? '0 0 0 2px rgba(0, 0, 0, 0.1)' : 'none',
										borderRadius: '6px',
										margin: '0 4px',
										padding: '6px 12px',
									}}
								>
									{val}
								</Button>
							))}
						</div>
					</Col>
				</Row>



				{/* Participation Status */}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '150px',
							textAlign: 'left',
							fontWeight: '500',
							whiteSpace: 'nowrap',
						}}
					>
						Participation Status
					</Form.Label>

					<Col
						md={{ span: 8, offset: 4 }}
						style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
					>
						<FormCheck
							id="participationStatusEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.participationStatusEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								handleChange('participationStatusEnabled', checked);
								if (!checked) {
									handleChange('participationStatus', null);
								} else if (!segment.participationStatus) {
									handleChange('participationStatus', { label: 'Active', value: 'active' });
								}
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div
							style={{
								width: '70%',
								marginLeft: 'auto',
								backgroundColor: '#f5f5f5',
								borderRadius: '8px',
								padding: '4px 8px',
							}}
						>
							<Select
								options={participationStatusOptions}
								value={participationStatusOptions.find(f => f.value === segment.participationStatus)}
								onChange={(val) => handleChange('participationStatus', val)}
								isDisabled={!segment.participationStatusEnabled}
								styles={{
									control: (provided) => ({
										...provided,
										border: 'none',
										backgroundColor: 'transparent',
										boxShadow: 'none',
									})
								}}
							/>
						</div>
					</Col>
				</Row>




				<h4 className="mt-4">Engagement & Interaction</h4>

				{/* Preferred Contact Method */}
				<Row className="mt-4 mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '200px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Preferred Contact Method
					</Form.Label>

					<Col
						md={{ span: 8, offset: 4 }}
						style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
					>
						<FormCheck
							id="contactMethodEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.contactMethodEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								handleChange('contactMethodEnabled', checked);
								if (!checked) {
									handleChange('contactMethod', null);
								} else if (!segment.contactMethod) {
									handleChange('contactMethod', 'Call');
								}
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div
							style={{
								width: '70%',
								marginLeft: 'auto',
								backgroundColor: '#f5f5f5',
								borderRadius: '8px',
								padding: '4px',
								display: 'flex',
								justifyContent: 'space-between',
								whiteSpace: 'nowrap',
							}}
						>
							{['Call', 'Text', 'Email'].map((val) => (
								<Button
									key={val}
									variant="light"
									onClick={() => handleChange('contactMethod', val)}
									disabled={!segment.contactMethodEnabled}
									style={{
										flex: 1,
										backgroundColor: segment.contactMethod === val ? '#fff' : '#f5f5f5',
										border: 'none',
										color: segment.contactMethod === val ? 'black' : '#666',
										fontWeight: segment.contactMethod === val ? '600' : 'normal',
										boxShadow: segment.contactMethod === val ? '0 0 0 2px rgba(0, 0, 0, 0.1)' : 'none',
										borderRadius: '6px',
										margin: '0 4px',
										padding: '6px 12px',
									}}
								>
									{val}
								</Button>
							))}
						</div>
					</Col>
				</Row>



				{/* Last Interaction Date*/}
				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							width: '200px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Last Interaction Date
					</Form.Label>

					<Col
						md={{ span: 8, offset: 4 }}
						style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
					>
						<FormCheck
							id="lastInteractionEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.lastInteractionEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								handleChange('lastInteractionEnabled', checked);
								if (!checked) {
									handleChange('lastInteraction', null);
								}
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div style={{ width: '70%', marginLeft: 'auto' }}>
							{dateField({
								get: ({ name }) => segment[name],
								set: ({ name, value }) => handleChange(name, value),
								getError: () => null,
								key: 'segment',
								name: 'lastInteraction',
								label: '',
								disabled: !(segment.lastInteractionEnabled ?? true),

								placeholder: 'DD/MM/YYYY',
								style: {
									backgroundColor: '#f5f5f5',
									borderRadius: '8px',
									padding: '6px 12px',
									border: 'none',
									fontSize: '14px',
								},
							})}
						</div>
					</Col>
				</Row>

				<h4 className="mt-4">Filters</h4>

				<Row className="mb-3" style={{ position: 'relative' }}>
					<Form.Label
						style={{
							position: 'absolute',
							left: 0,
							top: '20px',
							transform: 'translateY(-50%)',
							width: '200px',
							textAlign: 'left',
							fontWeight: '500',
						}}
					>
						Subject Filter
					</Form.Label>

					<Col
						md={{ span: 8, offset: 4 }}
						style={{ display: 'flex',  gap: '10px', marginTop: '8px' }}
					>
						<FormCheck
							id="subjectFilterEnabled"
							type="checkbox"
							label="Enable"
							checked={segment.subjectFilterEnabled ?? true}
							onChange={(e) => {
								const checked = e.target.checked;
								setSegment(prev => ({
									...prev,
									subjectFilterEnabled: checked,
									subject_filters_json: checked ? prev.subject_filters_json : null,
								}));
							}}
							style={{ marginLeft: '100px' }}
						/>

						<div style={{ width: '70%', marginLeft: 'auto', backgroundColor: '#f5f5f5', paddingTop: '10px', paddingBottom: '10px', borderRadius: '8px', opacity: segment.subjectFilterEnabled ? 1 : 0.5, pointerEvents: segment.subjectFilterEnabled ? 'auto' : 'none' }}>
							<FilterCreator
								get={() => segment.subject_filters_json}
								set={(val) =>
									setSegment(prev => ({
										...prev,
										subject_filters_json: val,
									}))
								}
							/>
						</div>
					</Col>
				</Row>



				<div className="d-flex justify-content-between mt-4">
					<Button onClick={() => navigate('/app/customers/segments')} variant="outline-secondary">Cancel</Button>
					<Button variant="primary" onClick={handleContinue}>Continue</Button>
				</div>
			</div>
		</>
	);

};

export default DataSegments;
